import { useEffect, useLayoutEffect, useMemo, useState, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import { useAccount, useMsal } from '@azure/msal-react';
import { createAppSessionFromMsalResult, Refresh } from 'services/auth/auth.service';
import { loginScopes } from 'config/msal.config';
import apiClient from 'services/_infra/apiClient';
import Cookies from 'js-cookie';

const AuthContextProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [accessToken, _setAccessToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasInitializedOnce, setHasInitializedOnce] = useState(false);

  const setAccessToken = useCallback((newToken) => {
    // Update isAuthenticated based on the presence of a token
    setIsAuthenticated(!!newToken);
    // Update cookie
    Cookies.set('isAuthenticated', !!newToken);

    if (newToken) {
      Cookies.set('accessToken', newToken);
    } else {
      Cookies.remove('accessToken');
    }
    _setAccessToken(newToken);
  }, []);

  // Centralized logout function
  const logout = useCallback(() => {
    setAccessToken(null);
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');
    Cookies.remove('isAuthenticated');
    Cookies.remove('email');
  }, [setAccessToken]);

  // Centralized login function
  const login = useCallback(
    (token, refreshToken = null) => {
      setAccessToken(token);
      if (refreshToken) {
        Cookies.set('refreshToken', refreshToken);
      }
    },
    [setAccessToken],
  );

  // Manual refresh function
  const refreshToken = useCallback(async () => {
    try {
      const response = await Refresh();
      if (response?.data?.accessToken) {
        setAccessToken(response.data.accessToken);
        return response.data.accessToken;
      }
      return null;
    } catch (error) {
      logout();
      return null;
    }
  }, [setAccessToken, logout]);

  // Initialize auth on mount - only if tokens exist
  useLayoutEffect(() => {
    const initAuth = async () => {
      const existingToken = Cookies.get('accessToken');
      const existingRefreshToken = Cookies.get('refreshToken');
      const shouldShowAnimation = !hasInitializedOnce && (existingToken || existingRefreshToken);

      if (existingToken) {
        // We have a token, set it synchronously
        _setAccessToken(existingToken);
        setIsAuthenticated(true);
      } else if (existingRefreshToken) {
        // No access token but have refresh token, try to refresh
        try {
          const response = await Refresh();
          if (response?.data?.accessToken) {
            _setAccessToken(response.data.accessToken);
            setIsAuthenticated(true);
            Cookies.set('accessToken', response.data.accessToken);
          } else {
            _setAccessToken(null);
            setIsAuthenticated(false);
            Cookies.remove('accessToken');
            Cookies.remove('refreshToken');
          }
        } catch {
          _setAccessToken(null);
          setIsAuthenticated(false);
          Cookies.remove('accessToken');
          Cookies.remove('refreshToken');
        }
      } else {
        // No tokens at all
        _setAccessToken(null);
        setIsAuthenticated(false);
      }

      // Wait minimum 1 second to show loading animation only on first initialization
      if (shouldShowAnimation) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Mark as initialized after all state updates
      setIsInitialized(true);
      setHasInitializedOnce(true);
    };

    initAuth();
  }, []);

  useEffect(() => {
    if (!children) return null;
    if (!account || inProgress !== 'none') return;

    instance
      .acquireTokenSilent({ ...loginScopes, account })
      .then(async (msalRes) => {
        await createAppSessionFromMsalResult(msalRes);
      })
      .catch((error) => {
        console.error('Error in microsoft MSAL Acquire Token Silent:', error);
      });
  }, [account, inProgress, instance, children]);

  //Note for self: useLayoutEffect blocks further processing while it runs. This insures that
  //      the interceptor is in place before any other components can make api calls.
  useLayoutEffect(() => {
    const interceptor = apiClient.interceptors.request.use((config) => {
      config.headers.Authorization =
        !config._retry && accessToken ? `Bearer ${accessToken}` : config.headers.Authorization;

      return config;
    });

    return () => {
      apiClient.interceptors.request.eject(interceptor);
    };
  }, [accessToken]);

  useLayoutEffect(() => {
    const interceptor = apiClient.interceptors.response.use(
      (response) => {
        if (response?.data?.message === 'User Logged Out Successfully') {
          logout();
        }

        if (response?.data?.accessToken) {
          setAccessToken(response.data.accessToken);
        }

        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Don't retry if:
        // 1. Already retried this request (_retry flag)
        // 2. Server indicates it already attempted refresh
        // 3. This is the refresh endpoint itself
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !error.response?.data?.attemptedRefresh &&
          !originalRequest.url?.includes('/fusionRefreshToken')
        ) {
          originalRequest._retry = true; // Mark as retried to prevent loops

          try {
            const refreshResponse = await Refresh();

            if (!refreshResponse || !refreshResponse.data || !refreshResponse.data.accessToken) {
              console.error('Invalid refresh response:', refreshResponse);
              logout();
              return Promise.reject(error);
            }

            setAccessToken(refreshResponse.data.accessToken);
            originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.accessToken}`;

            return apiClient(originalRequest);
          } catch (err) {
            console.error('Error refreshing token (Refresh):', err);
            logout();
            return Promise.reject(err);
          }
        }

        // If refresh already attempted or this is a refresh endpoint failure, reject
        if (
          error.response?.status === 401 &&
          (error.response?.data?.attemptedRefresh ||
            originalRequest.url?.includes('/fusionRefreshToken'))
        ) {
          logout();
        }

        return Promise.reject(error);
      },
    );

    return () => {
      apiClient.interceptors.response.eject(interceptor);
    };
  }, [setAccessToken, logout]);

  const value = useMemo(
    () => ({
      accessToken,
      isAuthenticated,
      isInitialized,
      setAccessToken,
      setIsAuthenticated,
      login,
      logout,
      refreshToken,
    }),
    [accessToken, isAuthenticated, isInitialized, setAccessToken, login, logout, refreshToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContextProvider;
